import s from './item.module.css'
import React, { memo, useState, useRef, useCallback, useEffect } from 'react'
import { PropTypes } from 'prop-types'
import Visibility from './visibility'
import { dimension } from './dimension'

const LazyItem = memo(function LazyItem({
    children,
    item,
    index,
    alwayVisible = false,
    scrollIntoView = false,
    mode = 'height',
    gridCellSize = 1,
    visibility
}) {
    const [st, setSt] = useState({
        //is it visible on initial render?
        visible: alwayVisible,
        measure: false,
        style: undefined
    })
    const stRef = useRef(st)
    stRef.current = st

    const ref = useRef(null)
    const scrollReq = useRef()
    const latest = useRef({ mode, gridCellSize, scrollIntoView })
    latest.current = { mode, gridCellSize, scrollIntoView }

    const onChangeVisibility = useCallback(visible => {
        const cur = stRef.current
        if (visible === cur.visible) return
        if (!visible && latest.current.mode == 'row-end-span') return //better performance but more ram use :(

        let style
        //measure height before hidding content (only in 'height' mode, and on second change of visibility)
        if (latest.current.mode == 'height') {
            const height = !visible && cur.measure && ref.current && ref.current.offsetHeight
            style = height ? { '--lazy-item-height': height + 'px' } : undefined
        }

        setSt({
            visible,
            measure: true,
            ...(style ? { style } : {})
        })
    }, [])

    const onChangeDimension = useCallback(({ height }) => {
        const gridRowEnd = `span ${Math.ceil(height / latest.current.gridCellSize)}`

        if (stRef.current.style?.gridRowEnd != gridRowEnd)
            setSt(cur => ({ ...cur, style: { gridRowEnd } }))
    }, [])

    const scrollToMe = useCallback(() => {
        cancelAnimationFrame(scrollReq.current)

        scrollReq.current = requestAnimationFrame(() => {
            const el = ref.current
            if (!el || !latest.current.scrollIntoView) return

            const { y } = el.getBoundingClientRect()
            if (y < 0 || y > window.innerHeight)
                el.scrollIntoView()
        })
    }, [])

    //visibility subscription — same DOM node as legacy bindRef registered
    useEffect(() => {
        const el = ref.current
        if (!alwayVisible && el)
            visibility.add(el, onChangeVisibility)
        else if (alwayVisible)
            setSt(cur => (cur.visible ? cur : { ...cur, visible: true }))

        return () => {
            if (el) visibility.remove(el)
        }
    }, [alwayVisible, visibility, onChangeVisibility])

    //dimension subscription on the rendered child (same node findDOMNode resolved)
    useEffect(() => {
        const el = ref.current && ref.current.firstElementChild
        if (!st.visible || mode != 'row-end-span' || !el) return

        dimension.add(el, onChangeDimension)
        return () => dimension.remove(el)
    }, [st.visible, mode, onChangeDimension])

    useEffect(() => {
        scrollToMe()
    }, [scrollToMe])

    const prevScroll = useRef(scrollIntoView)
    useEffect(() => {
        if (prevScroll.current != scrollIntoView)
            scrollToMe()
        prevScroll.current = scrollIntoView
    }, [scrollIntoView, scrollToMe])

    const { visible, style } = st

    return (
        <div
            ref={ref}
            className={(s.item || '') + ' ' + s[mode]}
            style={style}>
            {!!visible && children(item, index)}
        </div>
    )
})

LazyItem.propTypes = {
    //public
    children: PropTypes.func.isRequired,
    item: PropTypes.any.isRequired,
    index: PropTypes.number.isRequired,
    alwayVisible: PropTypes.bool,
    scrollIntoView: PropTypes.bool,

    mode: PropTypes.oneOf(['height', 'row-end-span']),
    gridCellSize: PropTypes.number,

    //internal
    visibility: PropTypes.instanceOf(Visibility),
}

export default LazyItem
